import mapboxgl from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles.scss';
import { INITIAL_ZOOM } from 'src/utils/constants';
import locationSvgUrl from 'src/assets/image/svg/location.svg?url';

type DraggableMapViewProps = {
   id: string;
   latitude: number;
   longitude: number;
   zoom?: number;
   hideAttControl?: boolean;
   onCoordinateChange?: (coords: { lat: number; lng: number }) => void;
};

const DraggableMapView: React.FC<DraggableMapViewProps> = ({
   id,
   latitude,
   longitude,
   zoom = INITIAL_ZOOM,
   hideAttControl = false,
   onCoordinateChange,
}) => {
   const mapRef: any = useRef(null);
   const mapContainerRef: any = useRef(null);
   const markerRef: any = useRef(null);
   const dragStartCoordsRef = useRef<{ lat: number; lng: number } | null>(null);
   const [style, setStyle] = useState<'streets' | 'satellite'>('streets');
   const [displayCoords, setDisplayCoords] = useState({ lat: latitude, lng: longitude });

   const styleUrls = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
   };

   useEffect(() => {
      if (!latitude || !longitude) return;
      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      mapRef.current = new mapboxgl.Map({
         container: mapContainerRef.current,
         style: styleUrls.streets,
         center: [longitude, latitude],
         zoom: zoom,
         interactive: true,
         attributionControl: !hideAttControl,
      });

      // Create draggable marker using SVG
      const el = document.createElement('div');
      el.style.cursor = 'grab';
      el.style.width = '40px';
      el.style.height = '49px';

      // Fetch and color the SVG — set `color` CSS property so `fill="currentColor"`
      // in the SVG resolves to --color-org-primary via CSS cascade.
      // This also auto-updates when the CSS variable changes (no JS re-render needed).
      el.style.color = 'var(--color-secondary)';
      fetch(locationSvgUrl)
         .then(res => res.text())
         .then(svg => {
            el.innerHTML = svg;
            const svgElement = el.querySelector('svg');
            if (svgElement) {
               svgElement.style.pointerEvents = 'none';
            }
         })
         .catch(() => {
            const img = document.createElement('img');
            img.src = locationSvgUrl;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.pointerEvents = 'none';
            el.appendChild(img);
         });

      markerRef.current = new mapboxgl.Marker(el, { draggable: true, anchor: 'center' }).setLngLat([longitude, latitude]).addTo(mapRef.current!);

      // Create popup for marker
      const popup = new mapboxgl.Popup({ offset: [0, -30], closeButton: false, closeOnClick: false })
         .setHTML('<div><div style="pointer-events: auto; background-color: white; padding: 5px; border-radius: 4px; font-size: 12px; font-weight: 500; color: #333; white-space: nowrap;">Draggable Map Pin</div></div>')
         .addClassName('marker-popup');
      markerRef.current.setPopup(popup);
      popup.addTo(mapRef.current!);

      // Handle drag events with sensitivity threshold
      const handleDragStart = () => {
         el.style.cursor = 'grabbing';
         dragStartCoordsRef.current = markerRef.current!.getLngLat();
         // Disable all map interactions while dragging marker
         mapRef.current!.dragPan.disable();
         mapRef.current!.dragRotate.disable();
         mapRef.current!.touchPitch.disable();
         mapRef.current!.doubleClickZoom.disable();
         mapRef.current!.boxZoom.disable();
         // Hide popup during drag
         if (popup.isOpen()) {
            popup.remove();
         }
      };

      const handleDrag = () => {
         const lngLat = markerRef.current!.getLngLat();
         // Only update if movement is significant (> 0.001 degrees ≈ 100m)
         if (dragStartCoordsRef.current) {
            const latDiff = Math.abs(lngLat.lat - dragStartCoordsRef.current.lat);
            const lngDiff = Math.abs(lngLat.lng - dragStartCoordsRef.current.lng);
            if (latDiff > 0.001 || lngDiff > 0.001) {
               setDisplayCoords({ lat: parseFloat(lngLat.lat.toFixed(4)), lng: parseFloat(lngLat.lng.toFixed(4)) });
               onCoordinateChange?.({ lat: lngLat.lat, lng: lngLat.lng });
            }
         }
      };

      const handleDragEnd = () => {
         el.style.cursor = 'grab';
         dragStartCoordsRef.current = null;
         // Re-enable all map interactions after marker drag
         mapRef.current!.dragPan.enable();
         mapRef.current!.dragRotate.enable();
         mapRef.current!.touchPitch.enable();
         mapRef.current!.doubleClickZoom.enable();
         mapRef.current!.boxZoom.enable();
         // Show popup again after drag (with slight delay to avoid conflicts)
         setTimeout(() => {
            if (!popup.isOpen()) {
               popup.addTo(mapRef.current!);
            }
         }, 100);
      };

      markerRef.current.on('dragstart', (e: any) => {
         e.preventDefault?.();
         handleDragStart();
      });
      markerRef.current.on('drag', (e: any) => {
         e.preventDefault?.();
         handleDrag();
      });
      markerRef.current.on('dragend', (e: any) => {
         e.preventDefault?.();
         handleDragEnd();
      });

      const resizeObserver = new ResizeObserver(() => {
         mapRef.current?.resize();
      });
      resizeObserver.observe(mapContainerRef.current);

      return () => {
         resizeObserver.disconnect();
         markerRef.current.off('dragstart', handleDragStart);
         markerRef.current.off('drag', handleDrag);
         markerRef.current.off('dragend', handleDragEnd);
         mapRef.current?.remove();
      };
   }, []);

   useEffect(() => {
      // The coordinate inputs are editable (NPT-1471); a half-typed "-" or ""
      // arrives here as NaN and mapbox throws on an invalid LngLat.
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      if (mapRef.current && markerRef.current) {
         const center = [longitude, latitude];
         mapRef.current.panTo(center, { duration: 1000 });
         markerRef.current.setLngLat([longitude, latitude]);
         setDisplayCoords({ lat: parseFloat(latitude.toFixed(4)), lng: parseFloat(longitude.toFixed(4)) });
      }
   }, [latitude, longitude]);

   const handleStyleToggle = (newStyle: 'streets' | 'satellite') => {
      if (newStyle !== style && mapRef.current) {
         setStyle(newStyle);
         mapRef.current.once('styledata', () => {
            if (markerRef.current && mapRef.current) {
               markerRef.current.addTo(mapRef.current);
            }
         });
         mapRef.current.setStyle(styleUrls[newStyle]);
      }
   };

   return (
      <div className="relative w-full flex-1 rounded-md overflow-hidden">
         {/* Map container */}
         <div className="w-full h-full" id={id} ref={mapContainerRef} />

         {/* Map/Satellite toggle buttons - top right */}
         <div className="absolute top-3 right-3 bg-white rounded shadow-md flex gap-0 z-10">
            <button
               onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStyleToggle('streets');
               }}
               className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  style === 'streets' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'
               }`}
            >
               Map
            </button>
            <button
               onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStyleToggle('satellite');
               }}
               className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${
                  style === 'satellite' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'
               }`}
            >
               Satellite
            </button>
         </div>

         {/* Coordinate display - bottom right */}
         <div className="absolute bottom-10 right-4 bg-white rounded shadow-md p-2.5 z-10 text-xs">
            <div className="text-gray-600 font-medium mb-1.5">Pin position</div>
            <div className="text-gray-900 font-semibold leading-tight">Latitude: {displayCoords.lat}</div>
            <div className="text-gray-900 font-semibold leading-tight">Longitude: {displayCoords.lng}</div>
         </div>
      </div>
   );
};

export default DraggableMapView;
